import { Injectable } from '@angular/core';
import { of, from, Observable, concat, filter } from 'rxjs';
import { withLatestFrom, map, mergeMap, catchError, tap } from 'rxjs/operators';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as FileListActions from '../actions/file-list.actions';
import { FileListService, TGetDataResult } from '../file-list/services/file-list.service';
import { StarredListService } from '../file-list/services/starred-list.service';

import { State, getFileListState } from '../reducers';
@Injectable()
export class FileListEffects {
  constructor(
    private files: FileListService,
    private starred: StarredListService,
    private actions$: Actions,
    private store$: Store<State>,
  ) {}

  public refresh$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(FileListActions.FILE_LIST_REFRESH),
      mergeMap((action: FileListActions.FileListRefreshAction) => {
        const cachedData = this.files.getCachedData(action.payload.sync_id, action.payload.hist_id);
        const cacheStream = cachedData && of(new FileListActions.FileListSuccessAction(cachedData));

        const freshDataStream = from(
          this.files.getData(
            action.payload.sync_id,
            action.payload.hist_id,
            action.payload.showdeleted,
            action.payload.is_vault,
          ),
        ).pipe(
          filter((data: TGetDataResult) => data.updateState),
          map((data) => data.pathlist),
          tap((data) => this.files.setCacheData(action.payload.sync_id, action.payload.hist_id, data)),
          map((data) => new FileListActions.FileListSuccessAction(data)),
          catchError((err) => of(new FileListActions.FileListErrorAction(err))),
        );

        return cacheStream ? concat(cacheStream, freshDataStream) : freshDataStream;
      }),
    );
  });

  public reload$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(FileListActions.FILE_LIST_RELOAD),
      withLatestFrom(this.store$.select(getFileListState)),
      map(([action, storeState]) => {
        return new FileListActions.FileListRefreshAction({
          sync_id: storeState.sync_id,
          hist_id: storeState.hist_id,
          showdeleted: storeState.showdeleted ? 1 : 0,
          offset_metaname_digest: '0',
          is_vault: storeState.is_vault,
        });
      }),
    );
  });

  public success$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType<FileListActions.FileListSuccessAction>(FileListActions.FILE_LIST_SUCCESS),
      withLatestFrom(this.store$.select(getFileListState)),
      map(([action, storeState]) => {
        if (action.sort) {
          return new FileListActions.FileListSortAction({
            active: storeState.predicate,
            direction: storeState.direction,
          });
        } else {
          return new FileListActions.FileListSetPathlistAction(storeState.pathlist);
        }
      }),
    );
  });

  public showDeleted$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(FileListActions.FILE_LIST_SHOW_DELETED),
      withLatestFrom(this.store$.select(getFileListState)),
      map(([action, storeState]) => {
        return new FileListActions.FileListRefreshAction({
          sync_id: storeState.sync_id,
          hist_id: storeState.hist_id,
          showdeleted: storeState.showdeleted ? 1 : 0,
          offset_metaname_digest: '0',
          is_vault: storeState.is_vault,
        });
      }),
    );
  });
  public showFavorites$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(FileListActions.FILE_LIST_SHOW_FAVORITES),
      mergeMap((action: FileListActions.FileListShowFavoritesAction) => {
        return from(this.starred.showFavoritesList(action.payload.syncId)).pipe(
          map((data) => new FileListActions.FileListSuccessAction(data, false)),
          catchError((err) => {
            return of(new FileListActions.FileListErrorAction(err));
          }),
        );
      }),
    );
  });
}
